import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const serviceLinks = [{
  name: 'Utilization Review',
  to: '/services/utilization-review',
  desc: 'Evidence-based medical necessity reviews'
}, {
  name: 'Medical Bill Review',
  to: '/services/bill-review',
  desc: 'Accurate pricing and compliance'
}, {
  name: 'Case Management',
  to: '/services/case-management',
  desc: 'Clinical advocacy from injury to recovery'
}, {
  name: 'Medical Triage Nurse',
  to: '/services/triage',
  desc: '24/7 nurse triage at the moment of injury'
}, {
  name: 'A+ Ancillary Services',
  to: '/services/ancillary',
  desc: 'Featured · One source for every ancillary need',
  featured: true
}];
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const closeTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // On non-home pages, keep navbar solid always so it's readable over light page heroes
  const solid = isScrolled || !isHome;
  const openServices = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setServicesOpen(true);
  };
  const scheduleCloseServices = () => {
    closeTimeoutRef.current = window.setTimeout(() => setServicesOpen(false), 120);
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${solid ? 'bg-brand-navy shadow-xl py-4' : 'bg-transparent py-7'}`} data-id="element-238" data-mp="31arminm6qzer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-239" data-mp="2wacco6144a1j">
        <div className="flex items-center justify-between" data-id="element-240" data-mp="3azrs4pv70t1x">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-id="element-241" data-mp="36319pvvx1ldc">
            <img src="https://cdn.magicpatterns.com/uploads/9pNo3tTwvFYBri2vFZFThu/Arissa_Cost_Solutions_Logo.png" alt="Arissa Cost Solutions" className={`w-auto object-contain transition-all duration-300 ${solid ? 'h-16 md:h-20' : 'h-20 md:h-24'}`} data-id="element-242" data-mp="364cfg18x9373" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-8" data-id="element-243" data-mp="2nagzxwrws6ze">
            {/* Services dropdown */}
            <div className="relative" onMouseEnter={openServices} onMouseLeave={scheduleCloseServices} data-id="element-244" data-mp="7j1djmljp7om">
              <button onClick={() => setServicesOpen(v => !v)} className="flex items-center text-white/90 hover:text-brand-gold transition-colors text-sm font-medium tracking-wide" data-id="element-245" data-mp="a522t9pces9s">
                Services
                <ChevronDown className={`w-3.5 h-3.5 ml-1.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-246" data-mp="15x7ec92vf1u" />
              </button>

              <AnimatePresence data-id="element-247" data-mp="swl7dkz0medx">
                {servicesOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.15
              }} className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-id="element-248" data-mp="xuwgtuleemvm">
                    <div className="w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden" data-id="element-249" data-mp="3ozko88m7dhnm">
                      <div className="h-1 bg-brand-gold" data-id="element-250" data-mp="3f3hlp1nfvh05"></div>
                      <div className="p-2" data-id="element-251" data-mp="3f4138h96g3dn">
                        {serviceLinks.map(s => <Link key={s.name} to={s.to} onClick={() => setServicesOpen(false)} className="block px-4 py-3 rounded-lg hover:bg-brand-paper transition-colors group" data-id="element-252" data-mp="3b3yz81fyyxv7">
                            <div className="flex items-center justify-between" data-id="element-253" data-mp="1uv1815gr182">
                              <span className="font-serif font-bold text-brand-navy group-hover:text-brand-gold transition-colors" data-id="element-254" data-mp="3u0rwe8s9ciut">
                                {s.name}
                              </span>
                              {s.featured && <span className="text-[10px] uppercase tracking-widest font-bold text-brand-gold bg-brand-gold/10 px-2 py-0.5 rounded" data-id="element-255" data-mp="28abr2qtmpnel">
                                  New
                                </span>}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-256" data-mp="2m9l4xjud45fe">
                              {s.desc}
                            </div>
                          </Link>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/about" className="text-white/90 hover:text-brand-gold transition-colors text-sm font-medium tracking-wide" data-id="element-257" data-mp="310bvkok7wygx">
              About
            </Link>
            <Link to="/contact" className="text-white/90 hover:text-brand-gold transition-colors text-sm font-medium tracking-wide" data-id="element-258" data-mp="39x2paubas18c">
              Contact
            </Link>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-6" data-id="element-259" data-mp="19xz6qskgrcoa">
            <a href="tel:8552601053" className="flex items-center text-white/90 hover:text-brand-gold transition-colors text-sm font-medium" data-id="element-260" data-mp="1om2d432f4qdm">
              <Phone className="w-4 h-4 mr-2" data-id="element-261" data-mp="1xlysfe2x4cw0" />
              855.260.1053
            </a>
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-5 py-2.5 rounded font-semibold text-sm hover:bg-white transition-colors shadow-sm" data-id="element-262" data-mp="1tkndq3ll1c2l">
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden" data-id="element-263" data-mp="d6do765ltha6">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-2" data-id="element-264" data-mp="d7gdxar6xdoi">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-265" data-mp="2ju48p11qvik3" /> : <Menu className="w-6 h-6" data-id="element-266" data-mp="2jwqr5vq6bih3" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-267" data-mp="2a5dwkwczqe7d">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-brand-navy border-t border-white/10 overflow-hidden" data-id="element-268" data-mp="2a2rrr4hhicsu">
            <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-269" data-mp="1r830tlqki2wo">
              {/* Services accordion */}
              <button onClick={() => setMobileServicesOpen(v => !v)} className="w-full flex items-center justify-between px-3 py-3 text-base font-medium text-white hover:text-brand-gold hover:bg-white/5 rounded-md" data-id="element-270" data-mp="1w5bhtizzc0wc">
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-271" data-mp="2e8xwagv8o4vm" />
              </button>
              <AnimatePresence data-id="element-272" data-mp="24iw7tnyz4kg6">
                {mobileServicesOpen && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="overflow-hidden pl-3" data-id="element-273" data-mp="1zlcucqa3obpa">
                    {serviceLinks.map(s => <Link key={s.name} to={s.to} onClick={() => {
                setMobileMenuOpen(false);
                setMobileServicesOpen(false);
              }} className="block px-3 py-2.5 text-sm text-white/80 hover:text-brand-gold border-l border-white/10" data-id="element-274" data-mp="3fzcotlq6f955">
                        {s.name}
                      </Link>)}
                  </motion.div>}
              </AnimatePresence>

              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-white hover:text-brand-gold hover:bg-white/5 rounded-md" data-id="element-275" data-mp="2zl6k08rsjrqn">
                About
              </Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-white hover:text-brand-gold hover:bg-white/5 rounded-md" data-id="element-276" data-mp="3ehnwx4fmpefb">
                Contact
              </Link>

              <div className="mt-4 pt-4 border-t border-white/10 flex flex-col space-y-4 px-3" data-id="element-277" data-mp="cxeqcwbqcu0f">
                <a href="tel:8552601053" className="flex items-center text-white font-medium" data-id="element-278" data-mp="gyi6ljxows26">
                  <Phone className="w-5 h-5 mr-3 text-brand-gold" data-id="element-279" data-mp="lq07y93mkuno" />
                  855.260.1053
                </a>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="bg-brand-gold text-brand-navy text-center px-5 py-3 rounded font-semibold w-full" data-id="element-280" data-mp="qns279rqssv1">
                  Get a Quote
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}